import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Recycle, ArrowLeft } from "@phosphor-icons/react";
import usePageTitle from "@/lib/usePageTitle";

const TONNEVA = {
  legal_name: "Tonneva GmbH i.G.",
  street: "Bahnhofstraße 42",
  zip: "79576",
  city: "Weil am Rhein",
  country: "Deutschland",
  contact_email: "hello@tonneva.de",
  contact_phone: "+49 7621 999 000",
  managing_director: "Florian Baier",
  hrb: "wird im Handelsregister eingetragen",
  court: "Amtsgericht Freiburg",
  vat_id: "wird bei Zuteilung nachgereicht",
};

const PAGES = {
  impressum: {
    title: "Impressum",
    sections: [
      {
        heading: "Angaben gemäß § 5 TMG",
        lines: [
          { value: TONNEVA.legal_name },
          { value: TONNEVA.street },
          { value: `${TONNEVA.zip} ${TONNEVA.city}` },
          { value: TONNEVA.country },
        ],
      },
      {
        heading: "Kontakt",
        lines: [
          { label: "Telefon", value: TONNEVA.contact_phone },
          { label: "E-Mail", value: TONNEVA.contact_email },
        ],
      },
      {
        heading: "Vertretungsberechtigt",
        lines: [{ label: "Geschäftsführer", value: TONNEVA.managing_director }],
      },
      {
        heading: "Registereintrag",
        lines: [
          { label: "Handelsregister", value: TONNEVA.hrb },
          { label: "Registergericht", value: TONNEVA.court },
          { label: "USt-IdNr.", value: TONNEVA.vat_id },
        ],
      },
      {
        heading: "Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV",
        lines: [{ value: TONNEVA.managing_director }],
      },
      {
        heading: "Haftungsausschluss",
        paragraphs: [
          `Tonneva ist eine SaaS-Plattform für Anbieter von Mülltonnenreinigung. Die eigentliche Reinigungsleistung wird durch selbstständige Dienstleister ("Mandanten") erbracht. Für Vertragsverhältnisse zwischen Kunde und Mandant ist ausschließlich der jeweilige Mandant verantwortlich. Impressum, AGB und Datenschutzerklärung des Mandanten finden Sie im jeweiligen Mandanten-Portal.`,
        ],
      },
      {
        heading: "EU-Streitschlichtung",
        paragraphs: [
          "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr/",
          "Wir sind nicht bereit oder verpflichtet, an einem Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
        ],
      },
    ],
  },
  agb: {
    title: "Allgemeine Geschäftsbedingungen (AGB) — Tonneva Plattform",
    sections: [
      { heading: "1. Geltungsbereich", paragraphs: [
        `Diese AGB regeln die Nutzung der Tonneva-Plattform durch Anbieter von Mülltonnenreinigung („Mandanten") sowie deren Endkunden.`,
        "Für den Reinigungsvertrag zwischen Kunde und Mandant gelten die AGB des jeweiligen Mandanten.",
      ]},
      { heading: "2. Vertragsgegenstand", paragraphs: [
        "Tonneva stellt Mandanten eine White-Label-SaaS-Lösung inklusive Kundenportal, CRM, Routing, Rechnungsstellung und Marketing-Werkzeugen zur Verfügung.",
      ]},
      { heading: "3. Registrierung und Konten", paragraphs: [
        "Mandanten registrieren sich über die Website oder auf Anfrage. Nach erfolgreicher Registrierung erhalten sie ein eigenes Backoffice.",
        "Kunden registrieren sich bei ihrem jeweiligen Mandanten.",
      ]},
      { heading: "4. Preise, Abrechnung", paragraphs: [
        "Es gelten die zum Zeitpunkt des Vertragsschlusses vereinbarten Konditionen. Die Abrechnung erfolgt monatlich per SEPA-Lastschrift oder Rechnung.",
      ]},
      { heading: "5. Kündigung", paragraphs: [
        "Der Mandantenvertrag kann mit einer Frist von 30 Tagen zum Monatsende gekündigt werden.",
      ]},
      { heading: "6. Haftung", paragraphs: [
        "Tonneva haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit.",
        "Für einfache Fahrlässigkeit haftet Tonneva nur bei Verletzung wesentlicher Vertragspflichten, begrenzt auf den vertragstypischen, vorhersehbaren Schaden.",
      ]},
      { heading: "7. Schlussbestimmungen", paragraphs: [
        "Es gilt deutsches Recht. Gerichtsstand ist, soweit gesetzlich zulässig, der Sitz von Tonneva.",
      ]},
    ],
  },
  datenschutz: {
    title: "Datenschutzerklärung — Tonneva Plattform",
    sections: [
      { heading: "1. Verantwortlicher", paragraphs: [
        `Verantwortlicher im Sinne der DSGVO ist ${TONNEVA.legal_name}, ${TONNEVA.street}, ${TONNEVA.zip} ${TONNEVA.city}.`,
        `Kontakt: ${TONNEVA.contact_email}`,
      ]},
      { heading: "2. Rollenverteilung", paragraphs: [
        "Für Daten, die im Kontext eines Mandantenportals verarbeitet werden, ist Tonneva Auftragsverarbeiter im Sinne von Art. 28 DSGVO. Verantwortlicher ist der jeweilige Mandant.",
        "Für die Plattform-Kernbetriebsdaten (Mandanten-Konten, Nutzungsstatistiken) ist Tonneva selbst Verantwortlicher.",
      ]},
      { heading: "3. Verarbeitete Daten", paragraphs: [
        "- Mandanten-Kontodaten (Name, E-Mail, Rechnungsdaten)",
        "- Endkunden-Daten (Name, Anschrift, Kontakt) — im Auftrag des jeweiligen Mandanten",
        "- Technische Metadaten (IP, Session-Cookies) für Authentifizierung und Sicherheit",
        "Rechtsgrundlagen: Art. 6 Abs. 1 lit. b und f DSGVO.",
      ]},
      { heading: "4. Dienstleister", paragraphs: [
        "Wir setzen sorgfältig ausgewählte Auftragsverarbeiter ein (Mapbox für Geocoding, Resend für E-Mail-Versand, MongoDB Atlas für Datenbank-Hosting).",
        "Mit allen Auftragsverarbeitern bestehen entsprechende Verträge nach Art. 28 DSGVO.",
      ]},
      { heading: "5. Speicherdauer", paragraphs: [
        "Personenbezogene Daten werden gelöscht, sobald der Zweck der Verarbeitung entfällt und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.",
      ]},
      { heading: "6. Betroffenenrechte", paragraphs: [
        "Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Wenden Sie sich hierfür an die im Impressum genannte Kontaktadresse.",
        `Bei Beschwerden kontaktieren Sie bitte die zuständige Aufsichtsbehörde.`,
      ]},
      { heading: "7. Cookies", paragraphs: [
        "Tonneva setzt ausschließlich technisch notwendige Cookies ein (Session-Cookie für Authentifizierung, HttpOnly, Secure).",
        "Es findet kein Tracking und keine Weitergabe an Dritte statt.",
      ]},
    ],
  },
};

export default function TonnevaLegalPage() {
  const location = useLocation();
  const page = location.pathname.replace("/", "") || "impressum";
  const content = PAGES[page] || PAGES.impressum;
  usePageTitle(content.title);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`tonneva-legal-${page}`}>
      <header className="border-b border-black/5">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-black">
              <Recycle size={18} weight="bold" className="text-white" />
            </div>
            <div className="font-heading font-bold tracking-tight text-lg">Tonneva</div>
          </Link>
          <Link to="/" className="text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100 text-zinc-600 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-14 w-full">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-4 bg-black/5 text-zinc-600">
            Rechtliches
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight" data-testid="legal-title">{content.title}</h1>
        </div>
        <div className="space-y-8">
          {content.sections.map((s, i) => (
            <section key={s.heading || i} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading text-xl font-bold tracking-tight mb-3">{s.heading}</h2>
              {s.lines && (
                <dl className="space-y-1 text-sm">
                  {s.lines.map((l, j) => (
                    <div key={j} className="flex gap-3">
                      {l.label && <dt className="text-zinc-500 min-w-[140px]">{l.label}</dt>}
                      <dd className="text-zinc-800">{l.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.paragraphs && s.paragraphs.map((p, j) => (
                <p key={j} className="text-sm text-zinc-700 leading-relaxed mb-2 whitespace-pre-line">{p}</p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <footer className="mt-16 border-t border-black/5 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap items-center justify-between text-xs text-zinc-500 gap-3">
          <div>© {new Date().getFullYear()} Tonneva</div>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:underline" data-testid="footer-impressum">Impressum</Link>
            <Link to="/agb" className="hover:underline" data-testid="footer-agb">AGB</Link>
            <Link to="/datenschutz" className="hover:underline" data-testid="footer-datenschutz">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
